/**
 * Cart persistence helpers.
 *
 * The customer portal keeps its cart in localStorage under `cartData`. Writers used to
 * store the *entire* product-detail payload per cart entry — every colour/size SKU in
 * `ProductPartArray.ProductPart` with its full price-break table — which blows the ~5MB
 * localStorage quota on any sizeable catalogue (QuotaExceededError on add-to-cart).
 *
 * `trimProductForCart` keeps only the fields cart consumers actually read, and prunes the
 * part/media/charge arrays down to the rows in that entry. The result is a strict subset
 * of the old shape, so consumers need no changes and carts written by older builds keep
 * working.
 *
 * Field inventory (every read of a cart entry's `item` in the app):
 *   MenuCart      id, productName, MediaContent, showColor, showSize, isCustomerSupplied,
 *                 itemCode, productId, vendorId, vendorName, productType, poType,
 *                 inhouseId, ProductPartArray, ChargeArray
 *   ViewCart      productName, showColor, showSize, uomDetails, uomIdOrder
 *   PlaceOrder    all of the above
 *   cartReducer   id
 */

export const CART_STORAGE_KEY = 'cartData'

type AnyRec = Record<string, any>

/** MediaContent arrives as an array from some endpoints and an object map from others. */
const asArray = (v: any): any[] =>
  Array.isArray(v) ? v : v && typeof v === 'object' ? Object.values(v) : []

/** Flattens `currentOrder` ([[row, row], [row]]) into a plain row list. */
export const flattenRows = (currentOrder: any): any[] =>
  asArray(currentOrder).reduce((acc: any[], group: any) => acc.concat(asArray(group)), [])

const partColor = (p: AnyRec) => p?.ColorArray?.Color?.colorName
const partSize = (p: AnyRec) => p?.ApparelSize?.labelSize

/**
 * Parts needed to serve `rows`.
 *
 * Consumers match a row to a part on (colour, size). Sizeless products (productType '2',
 * where the writer stamps size 'OSFA') match on colour alone, so when no part of a row's
 * colour carries that row's size we keep every part of that colour rather than dropping it.
 */
const partsForRows = (parts: any[], rows: any[]): any[] => {
  // No rows at all — a legacy object-shaped `currentOrder` flattens to nothing, and other
  // unexpected shapes could too. There is nothing to narrow by, so keep every part; the
  // per-part field stripping below still applies. Dropping them here would be permanent:
  // migrateStoredCart writes the result back at boot, and the order would then have no rows.
  if (!rows || !rows.length) return parts

  const keep = new Set<number>()
  rows.forEach(r => {
    const sameColor: number[] = []
    // Normalize to strings before comparing: numeric row size 6 matches SKU size "6"
    // Consumers (PlaceOrder.tsx:384, MenuCart.js:212) use loose equality which implicitly
    // does this; we make it explicit to prevent data loss during cart trimming.
    parts.forEach((p, i) => { if (String(partColor(p)) === String(r?.color)) sameColor.push(i) })
    const sameSize = sameColor.filter(i => String(partSize(parts[i])) === String(r?.size))
    ;(sameSize.length ? sameSize : sameColor).forEach(i => keep.add(i))
  })
  // Nothing matched. Keeping every part here looks defensive but is not: both consumers
  // match a row to a part on colourName AND labelSize, so if no part shares a row's colour
  // none can ever be matched. Retaining them buys no behaviour and costs ~73KB per entry —
  // 429 such entries is ~31MB, i.e. the QuotaExceededError this file exists to prevent.
  // The row is dropped from the order either way; see CART-22 in placeOrderPayload.test.
  return Array.from(keep).sort((a, b) => a - b).map(i => parts[i])
}

const trimPart = (p: AnyRec) => ({
  sku: p?.sku,
  ApparelSize: { labelSize: partSize(p) },
  ColorArray: { Color: { colorName: partColor(p) } },
  partPrice: {
    PartPriceArray: {
      // Order matters: consumers walk every break and keep the last one whose
      // minQuantity <= row quantity.
      PartPrice: asArray(p?.partPrice?.PartPriceArray?.PartPrice).map((b: AnyRec) => ({
        minQuantity: b?.minQuantity,
        price: b?.price,
        salePrice: b?.salePrice,
      })),
    },
  },
})

/**
 * Media needed to serve `rows`: index 0 (used verbatim as `quoteCustomImage` on the order
 * item, regardless of row colour) plus one entry per colour in the cart.
 */
const mediaForRows = (media: any[], rows: any[]) => {
  const colors = new Set(rows.map(r => r?.color))
  const seen = new Set<string>()
  return [media[0], ...media.filter(m => colors.has(m?.color))]
    .filter(Boolean)
    // De-dupe on (colour, url) — index 0 is often also one of the row colours. Keying on
    // url alone dropped a row's colour whenever two colours shared an image (or had none),
    // and the per-colour lookup at order time then wrote an empty image.
    .filter(m => {
      const key = `${m?.color}\u0000${m?.url}`
      return seen.has(key) ? false : (seen.add(key), true)
    })
    .map(m => ({ color: m?.color, url: m?.url }))
}

const trimCharge = (c: AnyRec) => {
  const prices = asArray(c?.ChargePriceArray?.ChargePrice).slice(0, 1)
  return {
    chargeName: c?.chargeName,
    chargeCode: c?.chargeCode,
    chargeDescription: c?.chargeDescription,
    ChargePriceArray: {
      ChargePrice: prices.length > 0
        ? prices.map((p: AnyRec) => ({ xMinQty: p?.xMinQty, price: p?.price, salePrice: p?.salePrice }))
        : [{ xMinQty: 0, price: 0, salePrice: 0 }],
    },
  }
}

/**
 * Reduces a full product detail to what the cart needs to display the entry and submit
 * it as an order/quote. `rows` are the entry's `currentOrder` rows, flattened.
 */
export const trimProductForCart = (product: AnyRec, rows: any[] = []): AnyRec => {
  if (!product || typeof product !== 'object') return product
  const parts = asArray(product.ProductPartArray?.ProductPart)
  const media = asArray(product.MediaContent)

  const trimmed: AnyRec = {
    id: product.id,
    productId: product.productId,
    itemCode: product.itemCode,
    productName: product.productName,
    inhouseId: product.inhouseId,
    vendorId: product.vendorId,
    vendorName: product.vendorName,
    productType: product.productType,
    poType: product.poType,
    isCustomerSupplied: product.isCustomerSupplied ?? '0',
    showColor: product.showColor,
    showSize: product.showSize,
    uomIdOrder: product.uomIdOrder,
    uomDetails: asArray(product.uomDetails).map((u: AnyRec) => ({ id: u?.id, name: u?.name })),
    MediaContent: mediaForRows(media, rows),
    ProductPartArray: { ProductPart: partsForRows(parts, rows).map(trimPart) },
  }

  // Mirror the source's presence rather than inventing containers. Consumers guard with
  // `if (item.ChargeArray?.Charge)`, and an empty array is truthy — always emitting one
  // made that branch fire for products that have no charges at all, which POSTed an empty
  // charge and closed the Place Order dialog while rows were still being created.
  if (product.ChargeArray) {
    trimmed.ChargeArray = { Charge: asArray(product.ChargeArray.Charge).map(trimCharge) }
  }
  // Nothing reads decorations off cart data (pages read them from the product in redux),
  // so the contents go — but the container is kept when the source had one, so any
  // truthiness or optional-chain check sees what it saw before.
  if (product.DecorationArray) {
    trimmed.DecorationArray = { Decoration: [] }
  }

  return trimmed
}

/** Builds a cart entry with its product already trimmed to the rows it carries. */
export const buildCartEntry = (product: AnyRec, currentOrder: any[]): AnyRec => ({
  currentOrder,
  item: trimProductForCart(product, flattenRows(currentOrder)),
})

/** Re-trims a whole cart. Safe to run on carts written by older builds. */
export const trimCartData = (cart: any[]): any[] =>
  asArray(cart).map(entry =>
    entry && entry.item
      ? { ...entry, item: trimProductForCart(entry.item, flattenRows(entry.currentOrder)) }
      : entry
  )

/** Reads the cart, tolerating absent, corrupt or truncated storage. */
export const readCartData = (): any[] => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    try { localStorage.removeItem(CART_STORAGE_KEY) } catch (_) { /* storage unavailable */ }
    return []
  }
}

export const isQuotaError = (e: any): boolean =>
  !!e && (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED' || e.code === 22 || e.code === 1014)

/**
 * Writes the cart without ever throwing into the render tree — an unguarded setItem is
 * what turned a full cart into a white screen. Returns whether the write landed.
 */
export const writeCartData = (cart: any[]): { ok: boolean; quotaExceeded: boolean } => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart))
    return { ok: true, quotaExceeded: false }
  } catch (e) {
    return { ok: false, quotaExceeded: isQuotaError(e) }
  }
}

export const CART_FULL_MESSAGE =
  'Your cart is too large to save. Please place or remove some items and try again.'

export const CART_SAVE_FAILED_MESSAGE =
  'We could not save your cart. Please check your browser settings and try again.'

/** Picks the message that matches why the write failed. */
export const cartWriteMessage = (result: { quotaExceeded: boolean }): string =>
  result && result.quotaExceeded ? CART_FULL_MESSAGE : CART_SAVE_FAILED_MESSAGE

/**
 * Boot-time migration: shrinks a cart written by an older build in place, so users who
 * are already at quota are not stuck.
 */
export const migrateStoredCart = (): any[] => {
  // Compares against the stored string rather than re-serialising the input, so an
  // already-trimmed cart costs one JSON.stringify on the boot path instead of two.
  let raw: string | null = null
  try {
    raw = localStorage.getItem(CART_STORAGE_KEY)
  } catch (e) {
    return []
  }
  if (!raw) return []

  let parsed: any
  try {
    parsed = JSON.parse(raw)
  } catch (e) {
    try { localStorage.removeItem(CART_STORAGE_KEY) } catch (_) { /* storage unavailable */ }
    return []
  }
  if (!Array.isArray(parsed)) return []
  if (!parsed.length) return parsed

  const trimmed = trimCartData(parsed)
  const next = JSON.stringify(trimmed)
  if (next !== raw) {
    try {
      localStorage.setItem(CART_STORAGE_KEY, next)
    } catch (e) {
      // Write failed: return parsed (untrimmed) so Redux and localStorage stay in sync.
      // Returning trimmed would diverge them — Redux gets trimmed, localStorage stays untrimmed,
      // causing cartReducer index matching to fail and cart data to corrupt on next edit.
      return parsed
    }
  }
  return trimmed
}
